import { getCurrentInstance, onBeforeMount, onBeforeUnmount, ref } from "vue";
import { Vue } from "vue-property-decorator";
import { useI18n } from "@composables/i18n";
import { NavigationGuardNext, Route } from "vue-router";
import { gtmTrackingService } from "@services/gtm-tracking.service";
import { Emitter } from "@utils/events";
import { onBeforeRouteLeave, onBeforeRouteUpdate } from "vue-router/composables";

const initialized = ref(false);
const print = ref(false);

export const useBaseView = () => {
  const app = getCurrentInstance();
  const { t } = useI18n(app);

  const dirty = () => false;

  const initialize = async () => {
    await Vue.nextTick();
    initialized.value = true;
    await afterInitialize();
  };

  const afterInitialize = async () => {
    // after initialize hook
  };

  const manualGTMUpdate = () => false;

  const setupPrinting = () => {
    window.onbeforeprint = () => (print.value = true);
    window.onafterprint = () => (print.value = false);
    Emitter.on("ci::page::print", () => (print.value = true));
  };

  onBeforeRouteUpdate(async (to: Route, from: Route, next: NavigationGuardNext) => {
    next(() => {
      if (!manualGTMUpdate()) gtmReinitialize(to, from);
    });
  });

  onBeforeRouteLeave(async (_to: Route, _from: Route, next: NavigationGuardNext) => {
    if (dirty()) {
      const response = await confirmLeave();
      if (response) next();
    } else {
      // Navigate to next view
      next();
    }
  });

  const gtmReinitialize = (to: Route, from: Route, options?: Indexable) => {
    if (from.name) {
      gtmTrackingService.reinitialize(from.fullPath, to.meta?.pageType, options);
    }
  };

  onBeforeMount(() => {
    window.addEventListener("beforeunload", beforeWindowUnload, true);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", beforeWindowUnload, true);
  });

  const beforeWindowUnload = (e: Event) => {
    if (dirty()) {
      e.preventDefault();
      e.returnValue = false;
    }
  };

  const confirmLeave = async (): Promise<boolean> => {
    const messageBoxOptions = {
      title: t("confirm_leave.attention") as string,
      okTitle: t("confirm_leave.leave") as string,
      okVariant: "danger",
      bodyClass: "text-sm",
      modalClass: "ci-modal ci-modal-flexible ci-modal-size-md",
      cancelTitle: t("action.cancel") as string,
    };
    return app?.proxy.$root.$bvModal.msgBoxConfirm(t("confirm_leave.unsaved_data") as string, messageBoxOptions);
  };

  return {
    initialized,
    print,
    dirty,
    initialize,
    afterInitialize,
    manualGTMUpdate,
    setupPrinting,
    onBeforeRouteUpdate,
    onBeforeRouteLeave,
    gtmReinitialize,
    onBeforeMount,
    onBeforeUnmount,
  };
};
